import * as React from 'react';
import { Switch, Route, RouteProps, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import Workspace from './workspace';
import Loader from './common/Loader/Loader';
import AuthContext from '../contexts/AuthContext';

import { WORKSPACE } from '../constants/routes';
import { getTenant } from '../actions/authentication.actions';
import { IApplicationState } from '../reducers';
import { getQueryString } from '@nexxt/common/services/TextService';
import checkAuthStatus from '@nexxt/common/firebase/auth/checkStatus';
import { AUTH_CONFIG } from '@nexxt/common/firebase/auth/config';
import { updateUserState } from '../actions/authentication.actions';
import {
    setTenant,
    setAccessType,
    setUserDetails
} from '../actions/authentication.actions';
import { getUserDetails } from '@nexxt/common/firebase/auth/userDetails';
import { getAccessDevMode } from '@nexxt/common/firebase/auth/userDetails';
import { MANAGE_PROFILE } from '../constants/routes';
import { setViewMode } from '../actions/header.actions';
import Tracker from '@openreplay/tracker';
import { PROD_DOMAIN } from '@nexxt/common/constants';

interface PrivateRouteProps extends RouteProps {
    component: React.ComponentType<RouteProps> | React.ComponentType<any>;
    isLoggedIn: boolean;
}

interface IRouterProps {
    getTenant: () => void;
    isLoggedIn: boolean;
    requestingUser: boolean;
    location: any;
    changePath: (path: string) => void;
    tenant: any;
    viewMode: string;
    setUserDetails: (user: any) => void;
    setTenant: (tenant: any) => void;
    setAccessType: (accessType: string) => void;
    userDetails: any;
    updateUserState: () => void;
    setViewMode: () => void;
}
let tracker: Tracker;
const PortalRouter = (props: IRouterProps) => {
    const [signedIn, setSignedIn] = React.useState(false);
    const { firebaseAuth } = React.useContext(AuthContext);

    React.useEffect(() => {
        const checkLogin = async (pollDelayInMs: number = 0) => {
            const authenticated = await checkAuthStatus(
                firebaseAuth,
                process.env.INCA_SERVER_URL,
                process.env.AUTH_URL,
                window.location.href
            );
            setSignedIn(authenticated);
            props.updateUserState();
            if (authenticated) {
                const userDetails = getUserDetails();
                const disableSessionRecording =
                    getAccessDevMode() ||
                    process.env.NODE_ENV === 'development';
                const domain = process.env.DOMAIN_NAME as string;

                if (
                    !disableSessionRecording &&
                    !tracker &&
                    domain === PROD_DOMAIN
                ) {
                    tracker = new Tracker({
                        projectKey: process.env.OPEN_REPLAY_KEY
                    });
                    tracker
                        .start()
                        .then(() => {
                            console.log('Begin openReplay...');
                            tracker.setUserID(String(userDetails?.id));
                            tracker.setMetadata('userDetails', userDetails);
                            tracker.setMetadata('Domain', domain);
                        })
                        .catch((e) =>
                            console.error(
                                `Failed to load openReplay due to: ${e}`
                            )
                        );
                }

                const tenant = userDetails?.defaultTenant;
                props.setTenant({ ...tenant, id: tenant.tenantId });
                props.setViewMode();
                if (
                    userDetails?.passwordExpired &&
                    window.location.href !==
                        `${process.env.PORTAL_URL}${MANAGE_PROFILE}?exp=1`
                ) {
                    window.location.href = `${process.env.PORTAL_URL}${MANAGE_PROFILE}?exp=1`;
                }
                if (pollDelayInMs)
                    setTimeout(async () => {
                        await checkLogin(pollDelayInMs);
                    }, pollDelayInMs);
            }
        };
        checkLogin();
    }, []);

    if (!signedIn) {
        return (
            <div className="login">
                <div className="login__box justify-content-center">
                    <Loader isFixed={true} transparent />
                </div>
            </div>
        );
    }

    return (
        <Switch>
            <Route
                key={props.tenant.tenantId}
                path={WORKSPACE}
                component={Workspace}
                {...props}
            />
        </Switch>
    );
};

const mapDispatchToProps = (dispatch) => ({
    getTenant: () => {
        dispatch(getTenant());
    },
    changePath: (path) => {
        dispatch(push(path));
    },
    setTenant: (tenant) => {
        dispatch(setTenant(tenant));
    },
    setAccessType: (tenant) => {
        dispatch(setAccessType(tenant));
    },
    setUserDetails: (user) => {
        dispatch(setUserDetails(user));
    },
    updateUserState: () => {
        dispatch(updateUserState());
    },
    setViewMode: () => {
        dispatch(setViewMode());
    }
});

const mapStateToProps = (store: IApplicationState) => {
    const {
        authReducer: { isLoggedIn, requestingUser, tenant },
        utilsReducer: { viewMode }
    } = store;
    return {
        isLoggedIn,
        requestingUser,
        tenant,
        viewMode
    };
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(PortalRouter)
);
